import { ErrorMessage, Field } from "formik";
import RcSlider from "rc-slider";
import IconButton from "../../../IconButton";
import MenuSelect from "../../../../sudokit/components/SudoSelect/MenuSelect";

export const AdvancedSettingsPanel = ({
  fieldOptionNameFromBase,
  setFieldValue,
  currentTemperature,
  currentFrequencyPenalty,
  currentPresencePenalty,
  currentMaxTokens,
  showTemperature = true,
  showAdaptiveThinking = false,
  isTemperatureCustom,
  isFrequencyPenaltyCustom,
  isPresencePenaltyCustom,
  isMaxTokensCustom,
  selectedEngineMaxTokens,
  minOutputTokens,
  maxTokenSliderStep,
  defaultMaxTokens,
  inferenceBlocksLength,
  numGenerationValue,
  numGenerationOptions,
  formatSliderValue,
  sliderVisualProps,
  advancedSettingDefaults,
}) => {
  return (
    <div className="advancedSettings advancedSettingsStack">
      {showAdaptiveThinking && (
        <div className="select thinkingEffortField">
          <h3>Thinking Effort</h3>
          <Field name={fieldOptionNameFromBase("bedrockAdaptiveThinkingEffort")}>
            {({ field }) => (
              <MenuSelect
                label="Thinking Effort"
                variant="tertiary"
                menuAlign="start"
                value={field.value === "low" ? "low" : "default"}
                options={[
                  { label: "Model default", value: "default" },
                  { label: "Low", value: "low" },
                ]}
                onChange={(value) => {
                  setFieldValue(field.name, value === "low" ? "low" : undefined);
                }}
              />
            )}
          </Field>
          <p>Low asks the model to spend less effort thinking, which may leave more room for the response. Thinking and response text share the Maximum Output Tokens allowance.</p>
        </div>
      )}

      {showTemperature && (
        <label className="select sliderField">
          <div className="sliderFieldHeader">
            <div className="sliderFieldTitle">
              <h3>Temperature</h3>
              {isTemperatureCustom && (
                <IconButton
                  src="/assets/restart.svg"
                  size={22}
                  iconSize={14}
                  smartFill={false}
                  ariaLabel="Restore temperature to default"
                  hoverTipText="Restore to default"
                  className="restoreSettingButton"
                  onClick={(e) => {
                    e.preventDefault();
                    setFieldValue(
                      fieldOptionNameFromBase("temperature"),
                      advancedSettingDefaults.temperature
                    );
                  }}
                />
              )}
            </div>
            <span>{formatSliderValue(currentTemperature)}</span>
          </div>
          <RcSlider
            {...sliderVisualProps}
            min={0}
            max={1}
            step={0.01}
            value={Math.min(1, Math.max(0, currentTemperature))}
            onChange={(value) => {
              const nextValue = Array.isArray(value) ? value[0] : value;
              setFieldValue(fieldOptionNameFromBase("temperature"), Number(nextValue));
            }}
            ariaLabelForHandle="Temperature"
          />
          <div className="sliderBounds sliderBounds--semantic">
            <span>Stricter rule-following, less creative</span>
            <span>More creative, but may be less coherent</span>
          </div>
          <ErrorMessage
            name={fieldOptionNameFromBase("temperature")}
            component="p"
            className="error"
          />
        </label>
      )}

      <label className="select sliderField">
        <div className="sliderFieldHeader">
          <div className="sliderFieldTitle">
            <h3>Frequency Penalty</h3>
            {isFrequencyPenaltyCustom && (
              <IconButton
                src="/assets/restart.svg"
                size={22}
                iconSize={14}
                smartFill={false}
                ariaLabel="Restore frequency penalty to default"
                hoverTipText="Restore to default"
                className="restoreSettingButton"
                onClick={(e) => {
                  e.preventDefault();
                  setFieldValue(
                    fieldOptionNameFromBase("frequencyPenalty"),
                    advancedSettingDefaults.frequencyPenalty
                  );
                }}
              />
            )}
          </div>
          <span>{formatSliderValue(currentFrequencyPenalty)}</span>
        </div>
        <RcSlider
          {...sliderVisualProps}
          min={0}
          max={1}
          step={0.01}
          value={Math.min(1, Math.max(0, currentFrequencyPenalty))}
          onChange={(value) => {
            const nextValue = Array.isArray(value) ? value[0] : value;
            setFieldValue(fieldOptionNameFromBase("frequencyPenalty"), Number(nextValue));
          }}
          ariaLabelForHandle="Frequency Penalty"
        />
        <div className="sliderBounds sliderBounds--semantic">
          <span>Allows repetition for consistency</span>
          <span>Avoids repetition, more varied wording</span>
        </div>
        <ErrorMessage
          name={fieldOptionNameFromBase("frequencyPenalty")}
          component="p"
          className="error"
        />
      </label>

      <label className="select sliderField">
        <div className="sliderFieldHeader">
          <div className="sliderFieldTitle">
            <h3>Presence Penalty</h3>
            {isPresencePenaltyCustom && (
              <IconButton
                src="/assets/restart.svg"
                size={22}
                iconSize={14}
                smartFill={false}
                ariaLabel="Restore presence penalty to default"
                hoverTipText="Restore to default"
                className="restoreSettingButton"
                onClick={(e) => {
                  e.preventDefault();
                  setFieldValue(
                    fieldOptionNameFromBase("presencePenalty"),
                    advancedSettingDefaults.presencePenalty
                  );
                }}
              />
            )}
          </div>
          <span>{formatSliderValue(currentPresencePenalty)}</span>
        </div>
        <RcSlider
          {...sliderVisualProps}
          min={0}
          max={1}
          step={0.01}
          value={Math.min(1, Math.max(0, currentPresencePenalty))}
          onChange={(value) => {
            const nextValue = Array.isArray(value) ? value[0] : value;
            setFieldValue(fieldOptionNameFromBase("presencePenalty"), Number(nextValue));
          }}
          ariaLabelForHandle="Presence Penalty"
        />
        <div className="sliderBounds sliderBounds--semantic">
          <span>Stays focused on existing context</span>
          <span>Introduces newer topics and ideas</span>
        </div>
        <ErrorMessage
          name={fieldOptionNameFromBase("presencePenalty")}
          component="p"
          className="error"
        />
      </label>

      <label className="select stopField">
        <div className="stopFieldHeader">
          <h3>Stop</h3>
          <span className="stopFieldHint">
            Stops generation when these words/symbols appear. Comma-separated.
          </span>
        </div>
        <Field
          name={fieldOptionNameFromBase("stop")}
          component="input"
        />
      </label>

      <label className="select generationsField">
        <h3>Number of Cards to Generate</h3>
        {inferenceBlocksLength > 1 ? (
          <p>{numGenerationValue} (multi-stage)</p>
        ) : (
          <div className="menu-select-wrapper menu-select-wrapper--cards-count">
            <MenuSelect
              key={`numgen-${numGenerationValue}`}
              label="Number of Cards to Generate"
              variant="tertiary"
              menuAlign="start"
              value={`${numGenerationValue}`}
              options={numGenerationOptions}
              onChange={(value) => {
                setFieldValue(fieldOptionNameFromBase("numGenerations"), parseInt(value, 10));
              }}
            />
          </div>
        )}
      </label>

      <label className="select sliderField maxTokensField">
        <div className="sliderFieldHeader">
          <div className="sliderFieldTitle">
            <h3>Maximum Output Tokens</h3>
            {isMaxTokensCustom && (
              <IconButton
                src="/assets/restart.svg"
                size={22}
                iconSize={14}
                smartFill={false}
                ariaLabel="Restore maximum output tokens to default"
                hoverTipText="Restore to default"
                className="restoreSettingButton"
                onClick={(e) => {
                  e.preventDefault();
                  setFieldValue(fieldOptionNameFromBase("maxTokens"), defaultMaxTokens);
                }}
              />
            )}
          </div>
          <span>{Math.round(Math.min(selectedEngineMaxTokens, Math.max(minOutputTokens, currentMaxTokens))).toLocaleString()}</span>
        </div>
        <p>Setting a maximum does not guarantee output, only creates a cap. Models may return fewer tokens than this.</p>
        <RcSlider
          {...sliderVisualProps}
          min={minOutputTokens}
          max={selectedEngineMaxTokens}
          step={maxTokenSliderStep}
          value={Math.min(selectedEngineMaxTokens, Math.max(minOutputTokens, currentMaxTokens))}
          onChange={(value) => {
            const nextValue = Array.isArray(value) ? value[0] : value;
            setFieldValue(fieldOptionNameFromBase("maxTokens"), Math.round(Number(nextValue)));
          }}
          ariaLabelForHandle="Maximum Output Tokens"
        />
        <div className="sliderBounds">
          <span>{minOutputTokens.toLocaleString()}</span>
          <span>{selectedEngineMaxTokens.toLocaleString()}</span>
        </div>
        <ErrorMessage
          name={fieldOptionNameFromBase("maxTokens")}
          component="p"
          className="error"
        />
      </label>
    </div>
  );
};
